import React from 'react'

import { AwardMark } from '../../../components/AwardMark'
import { Button } from '../../../components/Button'
import { Card } from '../../../components/Card'
import {
  Facility as FacilityIcon,
  Location as LocationIcon,
  Online as OnlineIcon,
  Tel as TelIcon,
  Walk as WalkIcon,
} from '../../../icons'
import { ProgramsFacilityCardProps } from '../types'
import { useImageMapper } from '../utils/useImageMapper'
import { ProgramCard } from './ProgramCard'

const toReadableDistance = (distance: number) => {
  if (distance < 1) {
    return `${(distance * 1000).toFixed(0)}m`
  }

  return `${distance.toFixed(1)}km`
}

export const ProgramsFacilityCard = (props: ProgramsFacilityCardProps) => {
  const {
    idGroup = 'facility_',
    id,
    href,
    as,
    external,
    target,
    titleAs,
    programTitleAs,
    imageMapper,
    isOnline,
    isFavorited,
    isFavoriteLoading,
    onFavoriteClick,
    name,
    prefecture,
    address1,
    address2,
    tel,
    distance,
    reviewScore,
    reviewCount,
    station,
    programs,
    programLinkBuilder,
    priority,
    awardYear,
  } = props

  const images = useImageMapper(props, imageMapper)
  const icon = !isOnline ? <FacilityIcon gradient /> : <OnlineIcon gradient />
  const [open, setOpen] = React.useState(false)
  const handleFavoriteClick = React.useCallback(() => {
    onFavoriteClick?.(id)
  }, [id, onFavoriteClick])

  const programCards = programs.map((program, i) => (
    <ProgramCard
      key={program.id}
      titleAs={programTitleAs}
      imageMapper={imageMapper}
      {...programLinkBuilder?.(program)}
      priority={i < 3 ? priority : undefined}
      {...program}
    />
  ))

  // First 3 cards
  const displayCards = programCards.slice(0, 3)
  // Rest
  const collapsedCards = programCards.slice(3)

  const facilityLink = {
    href,
    as,
    external,
    target,
  }

  return (
    <Card id={`${idGroup}${id}`}>
      <Card.Image images={images} {...facilityLink} priority={priority} />
      <Card.Content>
        {awardYear !== undefined && (
          <AwardMark year={awardYear} className="mb-1" />
        )}
        <Card.Content.Heading
          icon={icon}
          {...facilityLink}
          titleAs={titleAs}
          isFavorited={isFavorited}
          isFavoriteLoading={isFavoriteLoading}
          onFavoriteClick={handleFavoriteClick}
        >
          {name}
        </Card.Content.Heading>

        {prefecture || tel ? (
          <Card.Content.IconList
            mt={1}
            as="address"
            items={[
              ...(prefecture
                ? [
                    {
                      icon: <LocationIcon width="16px" height="16px" />,
                      text: `${prefecture}${address1 ?? ''}${address2 ?? ''}`,
                    },
                  ]
                : []),
              ...(tel ? [{ icon: <TelIcon />, text: tel }] : []),
              ...(distance
                ? [
                    {
                      icon: <WalkIcon />,
                      text: `現在地から${toReadableDistance(distance)}`,
                    },
                  ]
                : []),
            ]}
          />
        ) : null}

        <Card.Content.Review
          mt={1}
          score={reviewScore}
          count={reviewCount}
          rightContent={station}
        />

        {displayCards.length > 0 && (
          <Card.Content.CardList
            mt={2}
            openCollapsed={open}
            collapsedCards={collapsedCards}
          >
            {displayCards}
          </Card.Content.CardList>
        )}

        {collapsedCards.length > 0 && (
          <Card.Content.Body mt={3} centered>
            <Button
              variant="primary-outlined"
              rounded
              onClick={(e) => {
                e.preventDefault()
                setOpen(!open)
              }}
            >
              {!open ? `他 ${collapsedCards.length} 件を表示` : '閉じる'}
            </Button>
          </Card.Content.Body>
        )}
      </Card.Content>
    </Card>
  )
}
